import * as React from 'react';
import { useRef } from '@web-stories-wp/react';
import { useTransformHandler } from '../../../components/transform';
import { lightDirectionPreviewId } from '../utils/lightDirectionPreviewId';
import { BevelDepthFilter } from './bevelDepthFilter';
import { normalLightMatrix } from './normalBevelSampling';
import type { getTextDepth } from '../utils/getTextDepth';
import { textEffectFilterRegion } from './textEffectFilterRegion';

type Props = {
  readonly id: string;
  readonly depth: NonNullable<ReturnType<typeof getTextDepth>>;
  readonly width: number;
  readonly height: number;
  readonly previewElementId?: string;
  readonly previewDepthFor?: (angle: number) => ReturnType<typeof getTextDepth>;
  readonly cachePreview?: boolean;
};

// Shade only the fill. Strokes remain separate and the existing outer group
// still owns drop shadow. Each atop composite preserves the original alpha.
export function TextDepthFilter({
  id,
  depth,
  width,
  height,
  previewElementId,
  previewDepthFor,
  cachePreview = false,
}: Props) {
  const filterRef = useRef<SVGFilterElement>(null);
  useTransformHandler(
    previewElementId ? lightDirectionPreviewId(previewElementId) : null,
    (frame) => {
      const node = filterRef.current;
      const angle = frame?.updates?.lightDirection;
      if (
        !node ||
        (frame !== null &&
          (typeof angle !== 'number' || !Number.isFinite(angle)))
      )
        return;
      const preview =
        typeof angle === 'number' ? previewDepthFor?.(angle) : depth;
      if (!preview) return;
      if (preview.bevel) {
        node
          .querySelector('feDistantLight')
          ?.setAttribute('azimuth', String(preview.bevel.azimuth));
        node
          .querySelector('[data-normal-light]')
          ?.setAttribute(
            'values',
            normalLightMatrix(preview.bevel.azimuth, preview.bevel.altitude)
          );
        return;
      }
      for (const [result, sign] of [
        ['shifted', 1],
        ['opposite', -1],
      ] as const) {
        const offset = node.querySelector(`[result="${result}"]`);
        offset?.setAttribute('dx', String(sign * preview.dx));
        offset?.setAttribute('dy', String(sign * preview.dy));
      }
      const padding =
        Math.max(Math.abs(preview.dx), Math.abs(preview.dy)) +
        preview.softness * 3 +
        1;
      const region = textEffectFilterRegion(width, height, padding);
      for (const name of ['x', 'y', 'width', 'height'] as const) {
        node.setAttribute(name, String(region[name]));
      }
    }
  );
  if (depth.bevel)
    return (
      <BevelDepthFilter
        id={id}
        filterRef={filterRef}
        depth={depth.bevel}
        width={width}
        height={height}
        cachePreview={cachePreview}
      />
    );
  const padding =
    Math.max(Math.abs(depth.dx), Math.abs(depth.dy)) + depth.softness * 3 + 1;
  return (
    <filter
      ref={filterRef}
      id={id}
      {...textEffectFilterRegion(width, height, padding)}
      colorInterpolationFilters="sRGB"
    >
      <feOffset in="SourceAlpha" dx={depth.dx} dy={depth.dy} result="shifted" />
      <feComposite
        in="SourceAlpha"
        in2="shifted"
        operator="out"
        result="lightEdge"
      />
      <feGaussianBlur
        in="lightEdge"
        stdDeviation={depth.softness}
        result="softLight"
      />
      <feFlood floodColor="white" floodOpacity={depth.opacity} result="light" />
      <feComposite
        in="light"
        in2="softLight"
        operator="in"
        result="highlight"
      />
      <feComposite
        in="highlight"
        in2="SourceGraphic"
        operator="atop"
        result="litFill"
      />
      <feOffset
        in="SourceAlpha"
        dx={-depth.dx}
        dy={-depth.dy}
        result="opposite"
      />
      <feComposite
        in="SourceAlpha"
        in2="opposite"
        operator="out"
        result="darkEdge"
      />
      <feGaussianBlur
        in="darkEdge"
        stdDeviation={depth.softness}
        result="softDark"
      />
      <feFlood floodColor="black" floodOpacity={depth.opacity} result="dark" />
      <feComposite in="dark" in2="softDark" operator="in" result="shade" />
      <feComposite in="shade" in2="litFill" operator="atop" />
    </filter>
  );
}
